import type { CampaignAdProduct } from '@/types/spCampaigns';

export type NegativeKeywordRow = {
  negative_keyword_id: string;
  keyword_text: string;
  match_type: string;
  state: string;
  campaign_id?: string;
  ad_group_id?: string;
  /** SP: `ad_group` or `campaign`. Omitted/unknown treated as ad-group level. */
  product_level?: string | null;
};

export function isCampaignLevelNegative(row: Pick<NegativeKeywordRow, 'ad_group_id' | 'product_level'>): boolean {
  if (String(row.product_level || '').toLowerCase() === 'campaign') return true;
  return !row.ad_group_id;
}

export type NegativeKeywordsPayload = {
  negative_keywords: NegativeKeywordRow[];
  total_results: number;
  next_token?: string | null;
  start_index?: number;
  count?: number;
};

export type NegativeKeywordMutationResult = {
  index?: number;
  negative_keyword_id?: string | null;
  status: string;
  message?: string;
};

export type SpNegativeMatchType = 'NEGATIVE_EXACT' | 'NEGATIVE_PHRASE';
export type SbNegativeMatchType = 'negativeExact' | 'negativePhrase';

export function parseNegativeKeywordsPayload(data: unknown): NegativeKeywordsPayload {
  if (!data || typeof data !== 'object') {
    return { negative_keywords: [], total_results: 0 };
  }
  const raw = data as Record<string, unknown>;
  const items = Array.isArray(raw.negative_keywords)
    ? (raw.negative_keywords as NegativeKeywordRow[])
    : [];
  return {
    negative_keywords: items,
    total_results:
      typeof raw.total_results === 'number' ? raw.total_results : items.length,
    next_token: typeof raw.next_token === 'string' ? raw.next_token : null,
    start_index: typeof raw.start_index === 'number' ? raw.start_index : undefined,
    count: typeof raw.count === 'number' ? raw.count : undefined,
  };
}

export function parseNegativeKeywordMutationResults(
  data: unknown
): NegativeKeywordMutationResult[] {
  if (!data || typeof data !== 'object') return [];
  const raw = data as Record<string, unknown>;
  const results = raw.results;
  if (!Array.isArray(results)) return [];
  return results as NegativeKeywordMutationResult[];
}

export function formatNegativeMatchType(matchType: string): string {
  const normalized = matchType.toLowerCase().replace(/_/g, '');
  if (normalized.includes('phrase')) return 'Negative phrase';
  return 'Negative exact';
}

export function defaultNegativeMatchType(adProduct: CampaignAdProduct): SpNegativeMatchType | SbNegativeMatchType {
  return adProduct === 'SB' ? 'negativeExact' : 'NEGATIVE_EXACT';
}

export function normalizeNegativeStateForFilter(
  state: string,
  adProduct: CampaignAdProduct
): string {
  if (adProduct === 'SB') return state.toLowerCase();
  return state.toUpperCase();
}

export function toggleNegativeKeywordState(
  currentState: string,
  adProduct: CampaignAdProduct
): string {
  if (adProduct === 'SB') {
    return currentState.toLowerCase() === 'enabled' ? 'archived' : 'enabled';
  }
  return currentState.toUpperCase() === 'ENABLED' ? 'PAUSED' : 'ENABLED';
}

export function negativeKeywordStateLabel(state: string, adProduct: CampaignAdProduct): string {
  const normalized =
    adProduct === 'SB' ? state.toLowerCase() : state.toUpperCase();
  if (normalized === 'ENABLED' || normalized === 'enabled') return 'Enabled';
  if (normalized === 'PAUSED' || normalized === 'paused') return 'Paused';
  if (normalized === 'ARCHIVED' || normalized === 'archived') return 'Archived';
  return state || '-';
}
